import { useCallback, useEffect, useRef, useState } from "react";
import { campaignStatusTrackingAPI, CampaignStatusTrackingData } from "@/api/campaignStatusTracking";
import { useAuth } from "@/shared/contexts/AuthContext";
import { usePolling } from "@/shared/hooks/usePolling";

export function useCampaignStatusTracking(campaignId: string | null, options?: { enabled?: boolean }) {
	const enabled = options?.enabled !== false;
	const { isAuthenticated } = useAuth();
	const [data, setData] = useState<CampaignStatusTrackingData | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const lastGoodRef = useRef<CampaignStatusTrackingData | null>(null);
	const hasLoadedRef = useRef(false);

	// Rejects on failure so the poller can see a 401 and stop.
	const loadStatus = useCallback(
		async (opts?: { silent?: boolean }) => {
			if (!campaignId || !enabled) return;

			const silent = opts?.silent === true;
			if (!silent) setLoading(true);

			try {
				const next = await campaignStatusTrackingAPI.getByCampaignId(campaignId);
				lastGoodRef.current = next;
				setData(next);
				setError(null);
			} catch (err) {
				console.error("Failed to load campaign status tracking", err);
				setError("Unable to load campaign status");
				if (lastGoodRef.current) {
					setData(lastGoodRef.current);
				}
				throw err;
			} finally {
				if (!silent) setLoading(false);
			}
		},
		[campaignId, enabled],
	);

	// Public refetch never rejects; failures are reported through `error`.
	const fetchStatus = useCallback(
		async (opts?: { silent?: boolean }) => {
			try {
				await loadStatus(opts);
			} catch {
				// surfaced via error state
			}
		},
		[loadStatus],
	);

	useEffect(() => {
		hasLoadedRef.current = false;
		if (!campaignId || !enabled) {
			setData(null);
			setError(null);
		}
	}, [campaignId, enabled]);

	// First load shows the spinner; polls are silent. 30 s visible, 5 min hidden,
	// one fetch on return to the tab, stop on 401/403 (the session is gone).
	const poll = useCallback(() => {
		const silent = hasLoadedRef.current;
		hasLoadedRef.current = true;
		return loadStatus({ silent });
	}, [loadStatus]);

	usePolling(poll, { enabled: enabled && isAuthenticated && !!campaignId, key: campaignId });

	return {
		data,
		loading,
		error,
		refetch: fetchStatus,
	};
}
